import {
  approveDomain,
  generatePresignedUploadUrl,
  getBusinessOwnerById,
  getBusinessOwners,
  getProcessingImportUrlData,
  importUrlData,
  rejectDomain,
  revokeBadge,
  revokeProfile,
  unRevokeDomain,
  unRevokeProfile,
  updateBusinessOwner,
} from "@/services";
import { CertificationProfileUpdate, CertificationsParams } from "@/types";
import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";

const CERTIFICATIONS_QUERY_KEY = ["certifications"] as const;
const GET_BUSINESS_OWNER_QUERY_KEY = ["get-business-owner"] as const;
const GET_PROCESSING_IMPORT_URL_DATA_QUERY_KEY = ["get-processing-import-url-data"] as const;

export function useCertifications({
  offset = 0,
  limit = 10,
  profileType,
  status,
  search,
}: CertificationsParams) {
  return useQuery({
    queryKey: [CERTIFICATIONS_QUERY_KEY, offset, limit, profileType, status, search],
    queryFn: async () => {
      const response = await getBusinessOwners({
        offset,
        limit,
        profileType,
        status,
        search,
      });
      return response ?? null;
    },
    staleTime: 0,
    gcTime: 0,
  });
}

export function useGetBusinessOwner({ profileId }: { profileId: string }) {
  const {
    data: businessOwner,
    isFetching: isGetBusinessOwnerLoading,
    error: isGetBusinessOwnerError,
    refetch: refetchGetBusinessOwner,
  } = useQuery({
    queryKey: [GET_BUSINESS_OWNER_QUERY_KEY, profileId],
    queryFn: async () => {
      const response = await getBusinessOwnerById({
        profileId,
      });
      return response ?? null;
    },
    enabled: !!profileId,
    placeholderData: keepPreviousData,
    staleTime: 0,
    gcTime: 0,
  });

  return {
    businessOwner,
    isGetBusinessOwnerLoading,
    isGetBusinessOwnerError,
    refetchGetBusinessOwner,
  };
}

export function useUpdateBusinessOwner() {
  const {
    mutateAsync: mutationUpdateBusinessOwner,
    isPending: isUpdateBusinessOwnerLoading,
    isError: isUpdateBusinessOwnerError,
    isSuccess: isUpdateBusinessOwnerSuccess,
  } = useMutation({
    mutationFn: ({
      profile_id,
      profile_type,
      business,
      personal,
      verified_domains,
      status,
      admin_notes,
      reason_for_adjustment,
    }: CertificationProfileUpdate) =>
      updateBusinessOwner({
        profile_id,
        profile_type,
        business,
        personal,
        verified_domains,
        status,
        admin_notes,
        reason_for_adjustment,
      }),
  });

  return {
    mutationUpdateBusinessOwner,
    isUpdateBusinessOwnerLoading,
    isUpdateBusinessOwnerError,
    isUpdateBusinessOwnerSuccess,
  };
}

export function useRevokeBadge() {
  const {
    mutateAsync: mutationRevokeBadge,
    isPending: isRevokeBadgeLoading,
    isError: isRevokeBadgeError,
    isSuccess: isRevokeBadgeSuccess,
  } = useMutation({
    mutationFn: ({
      profileId,
      domain,
      reason,
    }: {
      profileId: string;
      domain: string;
      reason: string;
    }) => revokeBadge({ profileId, domain, reason }),
  });

  return {
    mutationRevokeBadge,
    isRevokeBadgeLoading,
    isRevokeBadgeError,
    isRevokeBadgeSuccess,
  };
}

export function useRevokeProfile() {
  const {
    mutateAsync: mutationRevokeProfile,
    isPending: isRevokeProfileLoading,
    isError: isRevokeProfileError,
    isSuccess: isRevokeProfileSuccess,
  } = useMutation({
    mutationFn: ({ profileId, reason }: { profileId: string; reason: string }) =>
      revokeProfile({ profileId, reason }),
  });

  return {
    mutationRevokeProfile,
    isRevokeProfileLoading,
    isRevokeProfileError,
    isRevokeProfileSuccess,
  };
}

export function useApproveDomain() {
  const {
    mutateAsync: mutationApproveDomain,
    isPending: isApproveDomainLoading,
    isError: isApproveDomainError,
    isSuccess: isApproveDomainSuccess,
  } = useMutation({
    mutationFn: ({ profileId, domain }: { profileId: string; domain: string }) =>
      approveDomain({ profileId, domain }),
  });

  return {
    mutationApproveDomain,
    isApproveDomainLoading,
    isApproveDomainError,
    isApproveDomainSuccess,
  };
}

export function useRejectDomain() {
  const {
    mutateAsync: mutationRejectDomain,
    isPending: isRejectDomainLoading,
    isError: isRejectDomainError,
    isSuccess: isRejectDomainSuccess,
  } = useMutation({
    mutationFn: ({
      profileId,
      domain,
      reason,
    }: {
      profileId: string;
      domain: string;
      reason: string;
    }) => rejectDomain({ profileId, domain, reason }),
  });

  return {
    mutationRejectDomain,
    isRejectDomainLoading,
    isRejectDomainError,
    isRejectDomainSuccess,
  };
}

export function useUnRevokeProfile() {
  const {
    mutateAsync: mutationUnRevokeProfile,
    isPending: isUnRevokeProfileLoading,
    isError: isUnRevokeProfileError,
    isSuccess: isUnRevokeProfileSuccess,
  } = useMutation({
    mutationFn: ({ profileId, reason }: { profileId: string; reason: string }) =>
      unRevokeProfile({ profileId, reason }),
  });

  return {
    mutationUnRevokeProfile,
    isUnRevokeProfileLoading,
    isUnRevokeProfileError,
    isUnRevokeProfileSuccess,
  };
}

export function useUnRevokeDomain() {
  const {
    mutateAsync: mutationUnRevokeDomain,
    isPending: isUnRevokeDomainLoading,
    isError: isUnRevokeDomainError,
    isSuccess: isUnRevokeDomainSuccess,
  } = useMutation({
    mutationFn: ({
      profileId,
      domain,
      reason,
    }: {
      profileId: string;
      domain: string;
      reason: string;
    }) => unRevokeDomain({ profileId, domain, reason }),
  });

  return {
    mutationUnRevokeDomain,
    isUnRevokeDomainLoading,
    isUnRevokeDomainError,
    isUnRevokeDomainSuccess,
  };
}

export function useImportUrlData() {
  const {
    mutateAsync: mutationImportUrlData,
    isPending: isImportUrlDataLoading,
    isError: isImportUrlDataError,
    isSuccess: isImportUrlDataSuccess,
  } = useMutation({
    mutationFn: ({
      isOverwrite,
      isEnhanceByAI,
      importedS3Key,
      originalFilename,
    }: {
      isOverwrite: boolean;
      isEnhanceByAI: boolean;
      importedS3Key: string;
      originalFilename: string;
    }) => importUrlData({ isOverwrite, isEnhanceByAI, importedS3Key, originalFilename }),
  });

  return {
    mutationImportUrlData,
    isImportUrlDataLoading,
    isImportUrlDataError,
    isImportUrlDataSuccess,
  };
}

export function useGetProcessingImportUrlData({ fileId }: { fileId: string }) {
  const {
    data: processingImportUrlData,
    isFetching: isGetProcessingImportUrlDataLoading,
    error: isGetProcessingImportUrlDataError,
    refetch: refetchGetProcessingImportUrlData,
  } = useQuery({
    queryKey: [GET_PROCESSING_IMPORT_URL_DATA_QUERY_KEY, fileId],
    queryFn: async () => {
      const response = await getProcessingImportUrlData({ fileId });
      return response ?? null;
    },
    enabled: !!fileId,
    // placeholderData: keepPreviousData,
    staleTime: 0,
    gcTime: 0,
  });

  return {
    processingImportUrlData,
    isGetProcessingImportUrlDataLoading,
    isGetProcessingImportUrlDataError,
    refetchGetProcessingImportUrlData,
  };
}

export function useGeneratePresignedUploadUrl() {
  const {
    mutateAsync: mutationGeneratePresignedUploadUrl,
    isPending: isGeneratePresignedUploadUrlLoading,
    isError: isGeneratePresignedUploadUrlError,
    isSuccess: isGeneratePresignedUploadUrlSuccess,
  } = useMutation({
    mutationFn: ({ fileExtension }: { fileExtension: string }) =>
      generatePresignedUploadUrl({ fileExtension }),
  });

  return {
    mutationGeneratePresignedUploadUrl,
    isGeneratePresignedUploadUrlLoading,
    isGeneratePresignedUploadUrlError,
    isGeneratePresignedUploadUrlSuccess,
  };
}
